import { useMemo } from 'react'

import type { TripItineraryData } from 'tripPlanner/api/types'
import {
  getExperienceItemOfferId,
  getStopExperienceItems,
  isBookedExperienceItem,
} from 'tripPlanner/utils/chatItinerary'
import { isExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'

export interface BookedExperienceSummary {
  name: string
  offerId?: string
}

export interface ExperienceTilePrice {
  currency?: string
  price: number
}

interface UseTripPlanExperienceTileDataProps {
  currentStop?: TripItineraryData['stops'][number]
}

export default function useTripPlanExperienceTileData({
  currentStop,
}: UseTripPlanExperienceTileDataProps) {
  const stopExperienceItems = useMemo(() => {
    return getStopExperienceItems(currentStop)
  }, [currentStop])

  const savedExperienceItems = useMemo(() => {
    return stopExperienceItems.filter((item) => isExperienceSavedData(item.savedItemData))
  }, [stopExperienceItems])

  const bookedExperienceItems = useMemo(() => {
    return stopExperienceItems.filter(isBookedExperienceItem)
  }, [stopExperienceItems])

  const bookedExperienceItem = bookedExperienceItems[0]

  const bookedExperienceSummary = useMemo<BookedExperienceSummary | undefined>(() => {
    if (!bookedExperienceItem) return undefined

    return {
      name: bookedExperienceItem.name,
      offerId: getExperienceItemOfferId(bookedExperienceItem),
    }
  }, [bookedExperienceItem])

  const savedExperiencePrice = useMemo(() => {
    // Only treat savedData.price as a from/per-person price when no concrete tickets
    // were selected; with ticket selections, price is the selected-tickets total.
    const firstPricedSavedExperience = savedExperienceItems.find((item) => {
      const savedData = item.savedItemData
      return isExperienceSavedData(savedData) &&
        !savedData.tickets?.length &&
        typeof savedData.price === 'number' &&
        savedData.price > 0
    })
    const savedData = firstPricedSavedExperience?.savedItemData
    if (!firstPricedSavedExperience || !isExperienceSavedData(savedData) || typeof savedData.price !== 'number') return undefined

    return {
      price: savedData.price,
      currency: savedData.currencyCode,
    }
  }, [savedExperienceItems])

  const savedExperienceCount = savedExperienceItems.length
  const hasSavedExperiences = savedExperienceCount > 0
  const bookedExperienceCount = bookedExperienceItems.length

  return {
    bookedExperienceSummary,
    bookedExperienceCount,
    hasSavedExperiences,
    experienceTilePrice: savedExperiencePrice,
    savedExperienceCount,
  }
}
